import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { api } from '../../services/api';
import Badge from '../ui/Badge';
import StateBlock from '../ui/StateBlock';
import CreateMeetingModal from '../CreateMeetingModal';
import { formatDateTime } from '../../utils/format';
import { MEETING_PAGE_SIZE, flattenPages, nextPageOffset } from '../../utils/meetingsModel';
import type { Tone } from '../ui/tone';

// P-38b (D-192 §12): the meetings of one customer (a tab on the customer page)
// or one lead (a section on the lead page). Checked on 2026-10-19 against the
// code: neither page had a meetings or interactions list, so this is the only
// one. The same list as /meetings, filtered to the customer / lead, 15 at a time.

const STATUS_TONE: Record<string, Tone> = { scheduled: 'info', completed: 'success', cancelled: 'neutral', rescheduled: 'warning' };

interface Row {
  id: string;
  title: string;
  meeting_datetime: string;
  status: string;
  meeting_type?: string | null;
  organizer_name?: string | null;
}

export default function EntityMeetings({
  organizationId,
  leadId,
  canCreate,
  bare = false,
}: {
  organizationId?: string;
  leadId?: string;
  canCreate: boolean;
  /** Inside a DetailSection that already has its own title: no heading of its own. */
  bare?: boolean;
}) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  const {
    data, isLoading, isError, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['meetings', 'entity', organizationId ?? '', leadId ?? ''],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api.getMeetingsPage({
      organization_id: organizationId, lead_id: leadId, limit: MEETING_PAGE_SIZE, offset: pageParam,
    }),
    getNextPageParam: (last, all) => nextPageOffset(last, all.reduce((n, p) => n + (p.data?.length ?? 0), 0)),
  });
  const rows = useMemo(() => flattenPages((data?.pages ?? []) as { data?: Row[] }[]), [data]);

  const addButton = canCreate ? (
    <button type="button" className="btn btn-secondary" onClick={() => setCreating(true)}>
      <Plus className="w-4 h-4" aria-hidden="true" />
      {t('meetings.createMeeting')}
    </button>
  ) : null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 sm:px-5 pt-1">
        {bare ? <span /> : <h2 className="text-base font-semibold text-surface-900">{t('meetings.title')}</h2>}
        {addButton}
      </div>

      {isLoading ? (
        <StateBlock kind="loading" size="panel" rows={3} />
      ) : isError ? (
        <StateBlock kind="error" size="panel" error={error} title={t('meetings.loadError')} onRetry={() => { void refetch(); }} />
      ) : rows.length === 0 ? (
        <p className="px-4 sm:px-5 py-6 text-sm text-surface-600">{t('meetings.emptyForEntity')}</p>
      ) : (
        <ul className="divide-y divide-surface-100">
          {rows.map((m) => (
            <li key={m.id}>
              <Link to={`/meetings/${m.id}`} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 sm:px-5 py-3 hover:bg-surface-50">
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-text"><bdi>{m.title}</bdi></span>
                  <span className="block text-xs text-surface-600">
                    <span dir="ltr" className="tabular-nums">{formatDateTime(m.meeting_datetime)}</span>
                    {m.meeting_type ? <> · {t(`meetings.type.${m.meeting_type}`)}</> : null}
                    {m.organizer_name ? <> · <bdi>{m.organizer_name}</bdi></> : null}
                  </span>
                </span>
                <Badge tone={STATUS_TONE[m.status] ?? 'neutral'} dot>{t(`meetings.status.${m.status}`)}</Badge>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {hasNextPage && (
        <div className="px-4 sm:px-5 pb-4">
          <button type="button" className="btn btn-secondary" disabled={isFetchingNextPage} aria-busy={isFetchingNextPage || undefined} onClick={() => { void fetchNextPage(); }}>
            {t('meetings.showMore')}
          </button>
        </div>
      )}

      {creating && (
        <CreateMeetingModal
          presetOrganizationId={organizationId}
          presetLeadId={leadId}
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  );
}
